/* eslint-disable no-undef */
/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 *
 * Singleton for the google maps api. Used to ensure that the google maps api is loaded once when needed.
 * Also, the instance of google maps should be the same in order to avoid the creation of multiple different maps
 * in the application or unnecessary reloads.
 */
import * as scriptjs from 'scriptjs';
import googleMapTheme from '../theme/googleMapsTheme.json';
import googleMapThemeDetailed from '../theme/googleMapsThemeDetailed.json';

// location in the middle of the Atlantic Ocean
export const INITIAL_LATITUDE = 23.065;
export const INITIAL_LONGITUDE = -37.415;
export const INITIAL_ZOOM = 3;
export const GOOGLE_MAP_INSTANCE_KEY = 'googleMapInstance';
const MINIMUM_ZOOM = 2;

class GoogleMapSingleton {
  public static loadGoogleMaps(handleLoaded: () => void, googleMapsURL: string, googleMapsAPIKey: string): void {
    if (!(typeof google === 'object' && typeof google.maps === 'object')) {
      scriptjs(`${googleMapsURL}${googleMapsAPIKey}`, handleLoaded);
    } else {
      handleLoaded();
    }
  }

  public static getInstance(): google.maps.Map {
    if (!window[GOOGLE_MAP_INSTANCE_KEY]) {
      const googleMapDiv = document.getElementById(GoogleMapSingleton.getDivId());
      if (googleMapDiv) {
        googleMapDiv.style.display = GoogleMapSingleton.isHidden ? 'none' : 'block';
        const mapTheme = googleMapTheme as google.maps.MapTypeStyle[];

        window[GOOGLE_MAP_INSTANCE_KEY] = new google.maps.Map(googleMapDiv, {
          center: { lat: INITIAL_LATITUDE, lng: INITIAL_LONGITUDE },
          zoom: INITIAL_ZOOM,
          minZoom: MINIMUM_ZOOM,
          disableDefaultUI: true, // turn off default controls to inject controls with custom styles
          styles: mapTheme,
          backgroundColor: '#3e4f5f',
        });
      }
    }

    return window[GOOGLE_MAP_INSTANCE_KEY];
  }

  public static removeInstance() {
    if (window[GOOGLE_MAP_INSTANCE_KEY]) {
      delete window[GOOGLE_MAP_INSTANCE_KEY];
    }
  }

  public static setDetailedTheme(): void {
    const map = window[GOOGLE_MAP_INSTANCE_KEY] as google.maps.Map;

    if (map) {
      map.setOptions({ styles: googleMapThemeDetailed as google.maps.MapTypeStyle[] });
    }
  }

  public static setSimpleTheme(): void {
    const map = window[GOOGLE_MAP_INSTANCE_KEY] as google.maps.Map;

    if (map) {
      map.setOptions({ styles: googleMapTheme as google.maps.MapTypeStyle[] });
    }
  }

  public static getDefaultMapTypeId(): google.maps.MapTypeId {
    return google.maps.MapTypeId.ROADMAP;
  }

  public static getSatelliteMapTypeId(): google.maps.MapTypeId {
    return google.maps.MapTypeId.HYBRID;
  }

  public static isHidden = true;

  public static showMapContainer() {
    const googleMapDiv = document.getElementById(GoogleMapSingleton.getDivId());
    if (googleMapDiv) googleMapDiv.style.display = 'block';
    GoogleMapSingleton.isHidden = false;
  }

  public static hideMapContainer() {
    const googleMapDiv = document.getElementById(GoogleMapSingleton.getDivId());
    if (googleMapDiv) googleMapDiv.style.display = 'none';
    GoogleMapSingleton.isHidden = true;
  }

  public static getDivId() {
    return 'googleMap';
  }
}

export default GoogleMapSingleton;
